import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import {
  MentorSlot,
  useGetAllMentorSlots,
  useUpdateAllMentorSlots,
  useUpdateMentorSlots,
  UserRole,
} from "@/lib/api/user";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { getInitials } from "@/lib/utils";
import { requirePermission } from "@/lib/casl/route-guards";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { CircularProgress } from "@/components/ui/circular-progress";
import { MentorMatchingSimulator } from "@/components/admin/mentor-matching-simulator";

export const Route = createFileRoute("/_authenticated/mentors/")({
  component: Mentors,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "mentor-slots");
  },
});

function Mentors() {
  const { data: mentorSlots, isPending } = useGetAllMentorSlots();
  const updateAllSlots = useUpdateAllMentorSlots();
  const { toast } = useToast();
  const [globalMaxTotal, setGlobalMaxTotal] = useState<number>(20);
  const [globalMaxDaily, setGlobalMaxDaily] = useState<number>(2);
  const [roleFilter, setRoleFilter] = useState<string>(UserRole.MENTOR);

  const filteredMentorSlots = useMemo(() => {
    if (!mentorSlots) return mentorSlots;
    
    return mentorSlots.filter((mentorSlot) => {
      if (roleFilter === "all") return true;
      return mentorSlot.mentor.role === roleFilter;
    });
  }, [mentorSlots, roleFilter]);

  const updateAllMentors = async () => {
    try {
      await updateAllSlots.mutateAsync({
        maxTotalSlots: globalMaxTotal,
        maxDailySlots: globalMaxDaily,
      });

      toast({
        title: "Success",
        description: "Updated slots for all mentors",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update slots for mentors",
        variant: "destructive",
      });
    }
  };

  if (isPending) {
    return (
      <div className="flex h-[500px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10">
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">Mentor Slots</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Manage mentor capacity and daily slot limits.{" "}
                  <Link to="/mentors/schedules" className="text-blue-600 hover:underline">
                    View Schedules
                  </Link>
                </p>
              </div>
              <div className="flex items-center gap-4">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value={UserRole.MENTOR}>Mentor</SelectItem>
              <SelectItem value={UserRole.ADMIN}>Admin</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Max Total Slots"
              value={globalMaxTotal}
              onChange={(e) => setGlobalMaxTotal(Number(e.target.value))}
              className="w-32"
            />
            <Input
              type="number"
              placeholder="Max Daily Slots"
              value={globalMaxDaily}
              onChange={(e) => setGlobalMaxDaily(Number(e.target.value))}
              className="w-32"
            />
          </div>
          <Button
            onClick={updateAllMentors}
            disabled={updateAllSlots.isPending}
          >
            {updateAllSlots.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Update All
          </Button>
          <MentorMatchingSimulator />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Mentor</TableHead>
            <TableHead>Active Mentorships</TableHead>
            <TableHead>Total Slots</TableHead>
            <TableHead>Daily Slots</TableHead>
            <TableHead>Slots Used Today</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredMentorSlots?.map((mentorSlot) => (
            <MentorRow key={mentorSlot.mentor.id} mentorSlot={mentorSlot} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MentorRow({ mentorSlot }: { mentorSlot: MentorSlot }) {
  const updateSlots = useUpdateMentorSlots();
  const { toast } = useToast();
  const [maxTotal, setMaxTotal] = useState<number>(
    mentorSlot.slot_config.max_total_slots,
  );
  const [maxDaily, setMaxDaily] = useState<number>(
    mentorSlot.slot_config.max_daily_slots,
  );

  useEffect(() => {
    setMaxTotal(mentorSlot.slot_config.max_total_slots);
    setMaxDaily(mentorSlot.slot_config.max_daily_slots);
  }, [
    mentorSlot.slot_config.max_total_slots,
    mentorSlot.slot_config.max_daily_slots,
  ]);

  const handleUpdate = async () => {
    try {
      await updateSlots.mutateAsync({
        userId: mentorSlot.mentor.id,
        data: {
          max_total_slots: maxTotal,
          max_daily_slots: maxDaily,
        },
      });

      toast({
        title: "Success",
        description: "Mentor slots updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update mentor slots",
        variant: "destructive",
      });
    }
  };

  const avatarUrl = useSignedMedia(mentorSlot.mentor.profile_picture_url);

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar>
            <AvatarImage src={avatarUrl?.url ?? ""} />
            <AvatarFallback>
              {getInitials(mentorSlot.mentor.name)}
            </AvatarFallback>
          </Avatar>
          <span>{mentorSlot.mentor.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span>{mentorSlot.active_mentorships_count}</span>
          {maxTotal > 0 && (
            <CircularProgress
              value={mentorSlot.active_mentorships_count}
              total={maxTotal}
            />
          )}
        </div>
      </TableCell>
      <TableCell>
        <Input
          type="number"
          value={maxTotal}
          onChange={(e) => setMaxTotal(Number(e.target.value))}
          className="w-24"
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          value={maxDaily}
          onChange={(e) => setMaxDaily(Number(e.target.value))}
          className="w-24"
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span>{mentorSlot.slot_config.slots_used_today}</span>
          {maxDaily > 0 && (
            <CircularProgress
              value={mentorSlot.slot_config.slots_used_today}
              total={maxDaily}
            />
          )}
        </div>
      </TableCell>
      <TableCell>
        <Button
          onClick={handleUpdate}
          variant="outline"
          size="sm"
          disabled={updateSlots.isPending}
        >
          {updateSlots.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Update
        </Button>
      </TableCell>
    </TableRow>
  );
}
